"use client";

import { ROUTES, withLocale } from "@/config/routes";
import { PaymentWaitingModal } from "@/features/wallet/components/PaymentWaitingModal";
import {
  ProviderIcon,
  getProviderLocaleKey,
} from "@/features/wallet/modules/balance-module/components/PaymentMethodCard";
import { mapRpcError } from "@/shared/lib/utils/error-mapper";
import { CURRENCY_CODE_LABEL, NETWORK_CODE_LABEL } from "@/shared/lib/utils/enum-labels";
import { paymentsClient } from "@/shared/rpc/clients";
import { resolveMethodName } from "@/features/wallet/utils/payment-method-name";
import { quickDepositStore } from "@/shared/stores/QuickDepositStore";
import { userStore } from "@/shared/stores/UserStore";
import { trackEvent } from "@/utils/tag-manager";
import { ArrowLeft, Cross, ShieldCheck } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { PaymentProvider } from "@cronika/proto/common/payment_pb";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRawContent,
  DropdownMenuTrigger,
  Input,
  addToast,
} from "@cronika/ui";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const MAXIMUM_DEPOSIT_AMOUNT = 50000;

const openPopup = (url: string): boolean => {
  try {
    const popup = window.open(url, "_blank");
    return !popup || popup.closed;
  } catch {
    return true;
  }
};

type TDepositDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export const DepositDrawer: FC<TDepositDrawerProps> = observer(({ open, onClose }) => {
  const { t, i18n } = useTranslation("deposit");
  const locale = i18n.language?.split("-")[0] ?? "en";
  const navigate = useNavigate();
  const user = userStore.user;

  const [amount, setAmount] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("usd");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user?.countryCode) return;
    quickDepositStore.load(user.countryCode);
    return () => quickDepositStore.reset();
  }, [open, user?.countryCode]);

  useEffect(() => {
    if (open && user?.promoCode && !promoCode) {
      setPromoCode(user.promoCode);
    }
  }, [open, user?.promoCode]);

  useEffect(() => {
    if (!open) {
      setPromoCodeError(null);
    }
  }, [open]);

  useEffect(() => {
    if (quickDepositStore.methods.length > 0 && !selectedProvider) {
      setSelectedProvider(quickDepositStore.methods[0]!.id);
    }
  }, [quickDepositStore.methods, selectedProvider]);

  // Sync currency when provider changes
  useEffect(() => {
    if (!selectedProvider) return;
    const provider = quickDepositStore.methods.find((p) => p.id === selectedProvider);
    if (provider) {
      const label = CURRENCY_CODE_LABEL[provider.currency as number] ?? "";
      setSelectedCurrency(label.toLowerCase() || "usd");
    }
  }, [selectedProvider, quickDepositStore.methods]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent("app:modal-open"));
    return () => {
      window.dispatchEvent(new CustomEvent("app:modal-close"));
    };
  }, [open]);

  const selectedProviderData = useMemo(
    () => quickDepositStore.methods.find((p) => p.id === selectedProvider),
    [quickDepositStore.methods, selectedProvider],
  );

  const quickAmounts = useMemo(() => {
    if (!selectedProviderData?.amounts) return [];
    return selectedProviderData.amounts.map(Number);
  }, [selectedProviderData]);

  const canEnterAmount = useMemo(() => {
    return selectedProviderData?.isAmountEditable !== false;
  }, [selectedProviderData]);

  const minimumAmount = useMemo(() => parseFloat(selectedProviderData?.minAmount ?? "0") || 10, [selectedProviderData]);

  const validationError = useMemo(() => {
    if (!amount) return null;
    const num = parseFloat(amount);
    if (Number.isNaN(num)) return t("validation.amount-invalid");
    if (num < minimumAmount) return t("validation.minimum-amount", { amount: minimumAmount });
    if (num > MAXIMUM_DEPOSIT_AMOUNT) return t("validation.maximum-amount", { amount: MAXIMUM_DEPOSIT_AMOUNT });
    return null;
  }, [amount, minimumAmount, t]);

  const canSubmit = useMemo(() => {
    if (!selectedProvider) return false;
    const num = parseFloat(amount);
    return !Number.isNaN(num) && num >= minimumAmount && num <= MAXIMUM_DEPOSIT_AMOUNT;
  }, [amount, minimumAmount, selectedProvider]);

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "" || /^[a-zA-Z0-9_-]*$/.test(value)) {
      setPromoCode(value.toUpperCase());
      if (promoCodeError) setPromoCodeError(null);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "" || /^\d+\.?\d*$/.test(value)) {
      const num = parseFloat(value);
      if (value === "" || (!Number.isNaN(num) && num <= MAXIMUM_DEPOSIT_AMOUNT)) {
        setAmount(value);
      }
    }
  };

  const handleConfirm = async () => {
    if (!canSubmit || !selectedProvider) return;
    try {
      setIsLoading(true);
      trackEvent("deposit", "deposit_form_submit", `deposit_form_submitted_${selectedProvider}`);

      const numAmount = parseFloat(amount);

      const responseData = await paymentsClient.createDeposit({
        paymentMethodId: selectedProvider,
        amount: String(numAmount),
        ...(promoCode ? { promoCode } : {}),
      });

      const redirectLink = responseData.paymentUrl;

      let popupBlocked = false;
      if (redirectLink) {
        popupBlocked = openPopup(redirectLink);

        if (popupBlocked) {
          setPaymentUrl(redirectLink);
          setIsPaymentModalOpen(true);
        }
      }

      if (responseData) {
        addToast({
          title: t("toast.success.title"),
          description: t("toast.success.description"),
          color: "success",
          timeout: 7000,
        });
        setAmount("");
        setPromoCode("");
        if (!popupBlocked) {
          onClose();
        }
      }
    } catch (e) {
      const { key } = mapRpcError(e);
      if (key === "validation:invalid-promo-code" || key.startsWith("wallet:bonus-details.search.error-")) {
        setPromoCodeError(t(key));
      } else {
        addToast({
          title: t("toast.error.title"),
          description: t("toast.error.description"),
          color: "danger",
          timeout: 7000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Transparent overlay */}
      <div
        className={`fixed inset-0 z-[200] transition-opacity duration-300 ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed z-[201] flex flex-col bg-[#141414E4] backdrop-blur-lg shadow-2xl transition-transform duration-300 ease-in-out
          bottom-0 left-0 right-0 h-[calc(100dvh-60px)] rounded-t-2xl border-t border-white/10
          sm:top-0 sm:bottom-auto sm:left-auto sm:h-full sm:w-[400px] sm:max-w-full sm:rounded-none sm:border-l sm:border-t-0
          ${open ? "translate-y-0 sm:translate-y-0 sm:translate-x-0" : "translate-y-full sm:translate-y-0 sm:translate-x-full"}`}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-white">{t("payment-method.label")}</span>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <Cross className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-24 space-y-2">
          {/* Title */}
          <h2 className="text-2xl font-bold text-white">{t("payment-method.label")}</h2>

          {/* Payment method list */}
          <div className="rounded-2xl">
            {quickDepositStore.loading ? (
              <div className="flex items-center justify-center py-6 text-neutral-400 text-sm">Loading...</div>
            ) : quickDepositStore.methods.length === 0 ? (
              <div className="flex items-center justify-center py-6 text-neutral-400 text-sm">
                {t("messages.no-payment-methods")}
              </div>
            ) : (
              quickDepositStore.methods.map((provider) => {
                const isSelected = selectedProvider === provider.id;
                return (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => {
                      trackEvent("deposit", "select_payment_method", provider.id);
                      setSelectedProvider(provider.id);
                      setAmount("");
                    }}
                    className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isSelected ? "payment-method-active" : "bg-transparent"
                    }`}
                  >
                    <div className="shrink-0 sm:w-10 sm:h-10 bg-[#3F3F4666] rounded-xl flex items-center justify-center p-2">
                      <ProviderIcon provider={provider} size="small" />
                    </div>
                    <span
                      className={`text-sm font-medium ${isSelected ? "bg-[radial-gradient(ellipse,#F89A09_0%,#FEC201_100%)] bg-clip-text text-transparent" : "text-white"}`}
                    >
                      {provider.provider === PaymentProvider.NOWPAYMENTS && provider.network
                        ? (() => {
                            const currencyLabel =
                              CURRENCY_CODE_LABEL[provider.currency as number] ?? String(provider.currency);
                            const networkLabel =
                              NETWORK_CODE_LABEL[provider.network as number] ?? String(provider.network);
                            return currencyLabel === networkLabel ? currencyLabel : `${currencyLabel} ${networkLabel}`;
                          })()
                        : (resolveMethodName(provider.nameTranslations, locale) ??
                          t(`provider.${getProviderLocaleKey(provider)}.name`, { defaultValue: provider.name }))}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* All methods link */}
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate(withLocale(ROUTES.WALLET) + "?tab=deposit");
            }}
            className="bg-[#F4A425]/10 rounded-xl flex items-center justify-center gap-2 w-full py-3 text-warning text-lg hover:opacity-80 transition-opacity"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            {t("buttons.all-methods")}
          </button>

          {/* Amount input */}
          {selectedProvider && canEnterAmount && (
            <Input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              label={t("amount.label", { currency: "USD" })}
              labelPlacement="inside"
              inputSize="lg"
              variant="flat"
              isRequired
              isInvalid={!!validationError}
              errorMessage={validationError ?? undefined}
              className="bg-[#27272A] text-white border-none"
              endContent={
                <span className="pointer-events-none text-gray-400 text-sm font-medium">
                  {selectedCurrency.toUpperCase()}
                </span>
              }
            />
          )}

          {/* Quick amounts */}
          {selectedProvider && quickAmounts.length > 0 && (
            <div className="flex gap-1.5">
              {quickAmounts.slice(0, 6).map((q: number) => {
                const num = parseFloat(amount);
                const isActive = !Number.isNaN(num) && num === q;
                return (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setAmount(q.toString())}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all ${
                      isActive
                        ? "bg-warning/20 border-warning text-warning"
                        : "bg-[#8C8C8C26] backdrop-blur-lg border-transparent text-white hover:border-zinc-600"
                    }`}
                  >
                    ${q}
                  </button>
                );
              })}
            </div>
          )}

          {selectedProvider &&
            quickAmounts.length > 6 &&
            (() => {
              const overflowAmounts = quickAmounts.slice(6);
              const numValue = parseFloat(amount);
              const isOverflowSelected = !Number.isNaN(numValue) && overflowAmounts.includes(numValue);
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen} modal={false}>
                    <DropdownMenuTrigger
                      asChild
                      className={`${isOverflowSelected && !isDropdownOpen ? "border border-warning" : "border-none"} h-10`}
                    >
                      <button
                        type="button"
                        className={`w-full py-2 px-3 transition-all text-sm flex items-center justify-between  backdrop-blur-sm rounded-lg data-[state=open]:rounded-tl-lg data-[state=open]:rounded-tr-lg data-[state=open]:rounded-bl-none data-[state=open]:rounded-br-none ${isOverflowSelected ? "bg-[#F4A42526] text-warning" : isDropdownOpen ? "bg-[#FFFFFF66]/45" : "bg-[#8C8C8C26]/45"}`}
                      >
                        <span>{isOverflowSelected ? `$${numValue}` : t("amount.other")}</span>
                        <svg
                          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="white"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuRawContent
                        className="z-[80] w-(--radix-dropdown-menu-trigger-width) overflow-y-auto rounded-br-lg rounded-bl-lg bg-[#8C8C8C26]/45 backdrop-blur-lg py-1 px-0 max-h-[180px]"
                        style={{ animation: "none", transition: "none" }}
                        align="start"
                        sideOffset={-1}
                      >
                        {overflowAmounts
                          .filter((quickAmount: number) => quickAmount !== numValue)
                          .map((quickAmount: number) => (
                            <DropdownMenuItem
                              key={quickAmount}
                              onClick={() => setAmount(quickAmount.toString())}
                              className="text-sm text-white font-light"
                            >
                              ${quickAmount}
                            </DropdownMenuItem>
                          ))}
                      </DropdownMenuRawContent>
                    </DropdownMenuPortal>
                  </DropdownMenu>
                </div>
              );
            })()}

          {/* Promo Code */}
          {selectedProvider && (
            <Input
              type="text"
              value={promoCode}
              onChange={handlePromoCodeChange}
              label={t("promoCode.label")}
              placeholder={t("promoCode.placeholder")}
              labelPlacement="outside"
              inputSize="lg"
              variant="flat"
              className="h-16 bg-[#27272A] text-white border-none"
              isInvalid={!!promoCodeError}
              errorMessage={promoCodeError ?? undefined}
            />
          )}

          {/* Deposit button */}
          <Button
            variant="glow"
            size="lg"
            className="w-full h-14 text-base font-bold rounded-2xl"
            disabled={!canSubmit}
            isLoading={isLoading}
            onClick={handleConfirm}
          >
            {canSubmit
              ? t("buttons.continue-pay", { amount, defaultValue: `Continue and Pay $${amount}` })
              : t("buttons.deposit")}
          </Button>

          {/* Security */}
          <div className="flex flex-row lg:flex-col lg:text-center items-center gap-3 pt-0.5 lg:pt-2">
            <ShieldCheck className="w-8 h-8 text-warning/60 shrink-0" />
            <p className="text-xs text-neutral-500 leading-relaxed">{t("security.message")}</p>
          </div>
        </div>
      </div>

      <PaymentWaitingModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        paymentUrl={paymentUrl}
      />
    </>
  );
});
